import React from "react";

import { Grid, MenuItem, SelectChangeEvent } from "@mui/material";
import { colors } from "@zapalacorp/tokens";
import { Add, MinusCirlce } from "iconsax-react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Button, Input, Select } from "../../../../../../../../../../components/Form";
import { LoadingSpinner } from "../../../../../../../../../../components/LoadingModal/styles";
import useYupValidation from "../../../../../../../../../../hooks/useYupValidation";
import { JobType } from "../../../../../../../../../../models/jobType";
import { StorageBoxTypeSealRule } from "../../../../../../../../../../models/storage.box.type.seal.rule";
import { Divider, Footer, RemoveRuleField, RuleField, RuleWrapper, useStyles } from "./styles";

interface IListStorageBoxRules {
  jobTypes?: JobType[];
  storageBoxTypeSealRules?: StorageBoxTypeSealRule[];
  isLoadingJobTypes: boolean;
  onChange: (attribute: string, index: number, value: JobType | number) => void;
  handleAddRule: () => void;
  disabled: boolean;
  errors: yup.ValidationError[];
  handleRemoveStorageBoxTypeRule: (id: number) => void;
}

export const ListStorageBoxRulesComponent = ({
  jobTypes,
  storageBoxTypeSealRules,
  onChange,
  handleAddRule,
  isLoadingJobTypes,
  disabled,
  errors,
  handleRemoveStorageBoxTypeRule,
}: IListStorageBoxRules) => {
  const { t: translate } = useTranslation();
  const classes = useStyles();

  if (isLoadingJobTypes && !storageBoxTypeSealRules) return <LoadingSpinner />;

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  function getJobTypeNameById(id: number): string | undefined {
    const job = jobTypes?.find((jobType) => jobType.id === id);

    return job?.name;
  }

  function handleSelectedJobTypes() {
    const jobTypesInStorageBoxTypeSealRule = storageBoxTypeSealRules?.map(
      (storageBoxTypeSealRule) => storageBoxTypeSealRule.jobTypeId
    );

    const selectedJobTypes = jobTypes?.filter((jobType) =>
      jobTypesInStorageBoxTypeSealRule?.includes(jobType.id)
    );

    return selectedJobTypes;
  }

  const selectedJobTypes = handleSelectedJobTypes();
  const disableAddNewRule = disabled || selectedJobTypes?.length === jobTypes?.length;

  return (
    <Grid container xs={12} spacing={2}>
      {storageBoxTypeSealRules &&
        storageBoxTypeSealRules.map((storageBoxRule, index) => {
          const selectedOptionName = getJobTypeNameById(storageBoxRule?.jobTypeId);

          return (
            <RuleWrapper
              container
              direction="row"
              alignItems="baseline"
              spacing={2}
              key={`${storageBoxRule?.jobTypeId}-rule-${index + 1}`}
            >
              <RuleField item xs>
                <Select
                  name="storageBoxRule"
                  label={translate("whenOrderTypeIs")}
                  value={selectedOptionName || ""}
                  onChange={(event: SelectChangeEvent<any>) => {
                    const selectedJobType = jobTypes?.find(
                      (jobtype) =>
                        jobtype.name.toLowerCase() === String(event.target.value).toLowerCase()
                    );

                    if (selectedJobType) onChange("jobType", index, selectedJobType);
                  }}
                  placeholder={translate("select")}
                  disabled={disabled}
                >
                  {jobTypes?.map((type) => (
                    <MenuItem
                      key={type?.id}
                      value={type.name}
                      disabled={selectedJobTypes?.includes(type)}
                    >
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </RuleField>
              <RuleField item xs>
                <Input
                  type="text"
                  label={translate("sealNumbersNeeded")}
                  placeholder={translate("numberOfSealsRequired")}
                  value={storageBoxRule.sealsQuantity}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    onChange("sealsQuantity", index, Number(event.target.value));
                  }}
                  disabled={disabled}
                  required
                  error={fieldHasError(`storageBoxTypeSealRules[${index}].sealsQuantity`)}
                  errorMessage={getErrorByField(`storageBoxTypeSealRules[${index}].sealsQuantity`)}
                />
              </RuleField>
              <RemoveRuleField item>
                <MinusCirlce
                  size="24"
                  color={colors.error500}
                  onClick={() => handleRemoveStorageBoxTypeRule(storageBoxRule?.jobTypeId)}
                  className={classes.icon}
                />
              </RemoveRuleField>
            </RuleWrapper>
          );
        })}
      <Divider />
      <Footer item xs={12} container justifyContent="flex-start">
        <Button
          className={classes.button}
          startIcon={
            <Add size="20" color={disableAddNewRule ? colors.gray500 : colors.brandBlue500} />
          }
          onClick={handleAddRule}
          disabled={disableAddNewRule}
        >
          {translate("addAnotherRule")}
        </Button>
      </Footer>
    </Grid>
  );
};
